/**
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: Dashboard
 *| (C) Copyright IBM Corp. 2017, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
define([
	'underscore'
], function(_) {
	'use strict';

	/*
		* This object represents the VIPR Properties orderer. It is built to be a singleton.
		* Its purpose is to set the order that properties will be displayed at within
		* the properties panel. We try to loosely couple the VIPR properties to
		* our product as much as possible so we will order as few properties as possible.
		*
		* Here is the levels being defined
		* 0 - 4 -> Color Palette and palette indexes (expecting more than defaultPaletteIndex to come)
		* 5 -> maintain axis scale
		* 6 -> all booleans
		* 10 - 14 -> itemAxis.label.visible, valueAxis.label.visible and titles.visible
		* 15 - 19 -> stacked and stacked %
		* 20 - 24 -> one of {valueLabels.visible and valueLabels.format} or {label.percentage and label.location}
		* 25 - 29 -> lines.visible, linesWithPoints.symbol, symbol.shape, markers.visible, markers.shape
		* 30 - 34 -> pie.has.hole and donutRadius
		* 35 - 39 -> legend position
		*/
	var VIPRPropertiesOrderer = (function () {

		/**
		 * Instance stores a reference to the Singleton
		 * @type {object}
		 */
		var instance = null;

		function init() {
			return {

				_setPropertyDescLevel: function(propertiesMap, id, position) {
					var desc = propertiesMap[id];
					if (desc) { desc['displayPos'] = position;}
				},
				/*
				* 0 - 4 -> Color Palette and palette indexes (expecting more than defaultPaletteIndex to come)
				*/
				_setLevel0To4: function(propertiesMap) {
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.title.visible', 0);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.title.visible', 0);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.title.visible', 0);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.ticks.labels.visible', 0);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.ticks.labels.visible', 0);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.tickInterval', 1);
					instance._setPropertyDescLevel(propertiesMap, 'colorPalette', 0);
					instance._setPropertyDescLevel(propertiesMap, 'colorPalette_column_defaultIndex', 0);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.ticks.visible', 1);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.ticks.visible', 1);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.tickInterval', 2);
					instance._setPropertyDescLevel(propertiesMap, 'defaultPaletteIndex', 1);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.title', 1);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.title.font', 2);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.ticks.labels.font', 2);
					instance._setPropertyDescLevel(propertiesMap, 'lineColor', 2);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.title.color', 4);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.title.visible', 5);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.title', 6);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.title.font', 7);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.title.color', 9);
				},

				/*
				* 6 -> all booleans
				*/
				_setBooleanDefaultOrder: function(propertiesMap) {
					_.each(propertiesMap, function(propertyDesc) {
						if (propertyDesc.type === 'CheckBox') {
							propertyDesc.displayPos = 6;
						}
					});
				},

				/*
				* 10 - 14 -> itemAxis.label.visible, valueAxis.label.visible and titles.visible
				*/
				_setLevel10To14: function(propertiesMap) {
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.minValue', 5);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.maxValue', 6);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.minValue', 5);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.maxValue', 6);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.minValue', 7);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.maxValue', 8);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.minValue', 7);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.maxValue', 8);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.labels.layoutMode', 13);
					instance._setPropertyDescLevel(propertiesMap, 'axis.x.alignment', 14);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.alignment', 14);
				},

				/*
				* 15 - 19 -> stacked and stacked %
				*/
				_setLevel15To19: function(propertiesMap) {
					instance._setPropertyDescLevel(propertiesMap, 'stacked', 15);
					instance._setPropertyDescLevel(propertiesMap, 'stacked.percent', 16);
				},

				/*
				* 20 - 24 -> one of {target.marker} or {labels.visible and label.format} or {label.percentage and label.location}
				*/
				_setLevel20To24: function(propertiesMap) {
					instance._setPropertyDescLevel(propertiesMap, 'target.marker.color', 0);
					instance._setPropertyDescLevel(propertiesMap, 'target.marker.thickness', 1);
					instance._setPropertyDescLevel(propertiesMap, 'label.location', 2);
					instance._setPropertyDescLevel(propertiesMap, 'label.percentage', 3);
					instance._setPropertyDescLevel(propertiesMap, 'valueLabels.visible', 4);
					instance._setPropertyDescLevel(propertiesMap, 'column.valueLabels.visible', 4);
					instance._setPropertyDescLevel(propertiesMap, 'column.valueLabels.location', 4);
					instance._setPropertyDescLevel(propertiesMap, 'column.valueLabels.orientation', 4);
					instance._setPropertyDescLevel(propertiesMap, 'column.valueLabels.color', 5);
					instance._setPropertyDescLevel(propertiesMap, 'bar.relativePadding', 5);
					instance._setPropertyDescLevel(propertiesMap, 'valueLabels.location', 5);
					instance._setPropertyDescLevel(propertiesMap, 'valueLabels.orientation', 6);
					instance._setPropertyDescLevel(propertiesMap, 'lineWithPoints.valueLabels.visible', 6);
					instance._setPropertyDescLevel(propertiesMap, 'lineWithPoints.valueLabels.color', 7);
					instance._setPropertyDescLevel(propertiesMap, 'valueLabels.format', 7);
					instance._setPropertyDescLevel(propertiesMap, 'contrast.label.color', 8);
					instance._setPropertyDescLevel(propertiesMap, 'bar.direction.vertical', 8);
					instance._setPropertyDescLevel(propertiesMap, 'lineWithPoints.interpolate', 9);
					instance._setPropertyDescLevel(propertiesMap, 'label.shadow', 9);
					instance._setPropertyDescLevel(propertiesMap, 'labels.format', 10);
					instance._setPropertyDescLevel(propertiesMap, 'labelLocation', 11);
					instance._setPropertyDescLevel(propertiesMap, 'leaf.labels.visible', 11);
					instance._setPropertyDescLevel(propertiesMap, 'renderingMode', 12);

					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.logarithmic', 13);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.logarithmic', 13);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.column.logarithmic', 14);
					instance._setPropertyDescLevel(propertiesMap, 'totalColumn.visible', 0);
					instance._setPropertyDescLevel(propertiesMap, 'totalColumn.label', 1);
					instance._setPropertyDescLevel(propertiesMap, 'subcategory.values', 2);
					instance._setPropertyDescLevel(propertiesMap, 'subcategory.totals', 3);
				},

				/*
				* 25 - 29 -> lines.visible, linesWithPoints.symbol, symbol.shape, markers.visible, markers.shape
				*/
				_setLevel25To29: function(propertiesMap) {
					instance._setPropertyDescLevel(propertiesMap, 'lines.visible', 25);
					instance._setPropertyDescLevel(propertiesMap, 'linesWithPoints.symbol', 26);
					instance._setPropertyDescLevel(propertiesMap, 'symbol.shape', 27);
					instance._setPropertyDescLevel(propertiesMap, 'symbolShape', 27);
					instance._setPropertyDescLevel(propertiesMap, 'markers.visible', 28);
					instance._setPropertyDescLevel(propertiesMap, 'lineWithPoints.display', 28);
					instance._setPropertyDescLevel(propertiesMap, 'lineWithPoints.symbol', 29);
				},

				/*
				* 30 - 34 -> pie.has.hole and donutRadius
				*/
				_setLevel30To34: function(propertiesMap) {
					instance._setPropertyDescLevel(propertiesMap, 'pie.has.hole', 30);
					instance._setPropertyDescLevel(propertiesMap, 'donutRadius', 31);
					instance._setPropertyDescLevel(propertiesMap, 'widget.legend.display', 32);
					instance._setPropertyDescLevel(propertiesMap, 'widget.legend.titleVisible', 33);
					instance._setPropertyDescLevel(propertiesMap, 'locationColor.title', 34);

				},

				/*
				* 35 - 39 -> legend position
				*/
				_setLevel35To39: function(propertiesMap) {
					instance._setPropertyDescLevel(propertiesMap, 'pointSize.title', 35);
					instance._setPropertyDescLevel(propertiesMap, 'pointColor.title', 36);
					instance._setPropertyDescLevel(propertiesMap, 'latlongSize.title', 37);
					instance._setPropertyDescLevel(propertiesMap, 'latlongColor.title', 38);
					instance._setPropertyDescLevel(propertiesMap, 'widget.legend.position', 39);
					instance._setPropertyDescLevel(propertiesMap, 'totalColumn.legend.label.positive', 40);
					instance._setPropertyDescLevel(propertiesMap, 'widget.legend.splitter', 40);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.title.font', 40);
					instance._setPropertyDescLevel(propertiesMap, 'itemAxis.ticks.labels.font', 40);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.title.font', 40);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.ticks.labels.font', 40);
					instance._setPropertyDescLevel(propertiesMap, 'valueAxis.line.ticks.labels.font', 40);
					instance._setPropertyDescLevel(propertiesMap, 'widget.legend.font', 41);
					instance._setPropertyDescLevel(propertiesMap, 'firstColumn.positive.label', 41);
					instance._setPropertyDescLevel(propertiesMap, 'firstColumn.negative.label', 41);
					instance._setPropertyDescLevel(propertiesMap, 'value.positive.label', 42);
					instance._setPropertyDescLevel(propertiesMap, 'value.negative.label', 42);
					instance._setPropertyDescLevel(propertiesMap, 'widget.legend.color', 43);
					instance._setPropertyDescLevel(propertiesMap, 'color.title', 44);
					instance._setPropertyDescLevel(propertiesMap, 'pointcolor.title', 44);
					instance._setPropertyDescLevel(propertiesMap, 'series.title', 44);
					instance._setPropertyDescLevel(propertiesMap, 'categories.title', 44);
					instance._setPropertyDescLevel(propertiesMap, 'columnColor.title', 44);
					instance._setPropertyDescLevel(propertiesMap, 'lineColor.title', 44);
					instance._setPropertyDescLevel(propertiesMap, 'values.title', 45);
					instance._setPropertyDescLevel(propertiesMap, 'size.title', 46);
					instance._setPropertyDescLevel(propertiesMap, 'color.min', 47);
					instance._setPropertyDescLevel(propertiesMap, 'color.mid', 48);
					instance._setPropertyDescLevel(propertiesMap, 'color.max', 49);
				},

				/**
				* An abstraction layer to hide how we order the properties in VIPR.
				* @param propertiesMap - map of property ids to property descriptions
				*/
				orderProperties: function(propertiesMap) {

					// _.each(propertiesMap, function(value, key){console.debug(key + ':' + JSON.stringify(value));})
					instance._setLevel0To4(propertiesMap);
					// TODO: maintainAxisScale
					instance._setBooleanDefaultOrder(propertiesMap);
					instance._setLevel10To14(propertiesMap);
					instance._setLevel15To19(propertiesMap);
					instance._setLevel20To24(propertiesMap);
					instance._setLevel25To29(propertiesMap);
					instance._setLevel30To34(propertiesMap);
					instance._setLevel35To39(propertiesMap);
				}
			};
		}

		return {

			// Get the Singleton instance if one exists
			// or create one if it doesn't
			getInstance: function () {

				if (!instance) {
					instance = init();
				}

				return instance;
			}
		};

	})();
	return VIPRPropertiesOrderer;
});
